import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { MediaTypeBadges, IMAGE_BADGES } from "@/components/Badges";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, Sparkles, Lock } from "lucide-react";

const stripHtml = (h) => (h || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "";
const fmtDates = (a, b) => a && b ? `${fmtDate(a)} – ${fmtDate(b)}` : fmtDate(a) || fmtDate(b);

const FILTERS = [
  { key: "all", label: "All" },
  { key: "dance_show", label: "Dance Shows" },
  { key: "dance_competition", label: "Competitions" },
  { key: "event", label: "Events" },
];

const HERO = "https://images.unsplash.com/photo-1547153760-18fc86324498?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Storefront() {
  const [products, setProducts] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/events").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  }, []);

  const filtered = (products || []).filter((p) => filter === "all" || p.category === filter);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0">
          <img src={HERO} alt="Dancer under stage light" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c] via-[#0a0a0c]/85 to-[#0a0a0c]/40" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-6 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-28">
          <div className="lg:col-span-8">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 font-mono-x text-xs uppercase tracking-widest text-rose-400">
              <Sparkles className="h-3.5 w-3.5" /> Media for Dancers
            </div>
            <h1 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
              Capture every
              <br />
              <span className="text-[#ff2a5f]">moment.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300">
              The MX Media Portal keeps your media in one place. Purchase
              photos, films and downloads from your dance shows, competitions and events — delivered
              instantly to your private library, no password to remember, just a sign-in link.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-browse"
                onClick={() => document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]"
              >
                Browse media <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-login"
                variant="outline"
                onClick={() => navigate(user ? "/downloads" : "/login")}
                className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800"
              >
                Access My Downloads
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section id="catalog" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">The Catalog</div>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
              Events &amp; shows
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  filter === f.key
                    ? "border-[#ff2a5f] bg-[#ff2a5f] text-white"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {products === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-20 text-center text-zinc-500">
            No events in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {filtered.map((p) => (
              <EventCard key={p.id} p={p} onOpen={() => navigate(`/event/${p.id}`)} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

const CATEGORY = {
  dance_show: { label: "Dance Show", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
  dance_competition: { label: "Competition", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  event: { label: "Event", cls: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
};

function EventCard({ p, onOpen }) {
  const badgeTypes = (p.storefront_badges && p.storefront_badges.length)
    ? p.storefront_badges
    : [...(p.content_types || []), ...(p.livestream ? ["livestream"] : []), ...(p.delivery_download ? ["download"] : [])];
  const shortText = p.short_description?.trim() ? p.short_description : stripHtml(p.description);
  const imgBadge = IMAGE_BADGES[p.image_badge];
  return (
    <div
      data-testid={`event-card-${p.id}`}
      onClick={onOpen}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/80 transition-all duration-300 hover:-translate-y-1 hover:border-rose-500/50 hover:shadow-2xl hover:shadow-rose-950/20"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-zinc-950">
        {(p.cover_image_url || p.thumbnail_url) ? (
          <img
            src={p.cover_image_url || p.thumbnail_url}
            alt={p.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-zinc-700">No preview</div>
        )}
        {imgBadge && (
          <div className="absolute left-3 top-3">
            <span data-testid={`image-badge-${p.id}`} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${imgBadge.cls}`}>{imgBadge.label}</span>
          </div>
        )}
        {p.has_password && (
          <div className="absolute right-3 top-3 rounded-full border border-amber-500/30 bg-black/50 p-1.5 text-amber-400">
            <Lock className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col space-y-3 p-5">
        <h3 className="font-display text-lg font-bold leading-snug text-white">{p.name}</h3>
        {badgeTypes.length > 0 && <MediaTypeBadges types={badgeTypes} testidPrefix={`card-badge-${p.id}`} />}
        {(p.start_date || p.end_date) && <div data-testid={`event-dates-${p.id}`} className="text-xs font-medium text-zinc-400">{fmtDates(p.start_date, p.end_date)}</div>}
        <p data-testid={`card-short-desc-${p.id}`} className="line-clamp-4 text-sm text-zinc-400">{shortText}</p>
        <div className="mt-auto flex items-center justify-end pt-2">
          <Button data-testid={`open-event-${p.id}`} onClick={onOpen} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
            Purchase Media
          </Button>
        </div>
      </div>
    </div>
  );
}
